import {
  Dialog,
  DialogMaxWidth,
  Grid,
  InfoIcon,
  Text,
  TextHighlight,
  TextSize
} from '@focaldata/cin-ui-components'
import React, { useState } from 'react'
import {
  ScreeningRuleDraft,
  ScreeningRuleRow
} from '../../../data/model/screeningRules'
import AudienceConditionRows, { emptyRow } from '../AudienceConditionRows'
import useStyles from '../audienceConditionRows.styles'
import {
  SourceQuestion,
  isDraftComplete,
  questionsThatScreenOutFirst,
  targetingQuestionsReferenced
} from './screeningRules.utils'

interface Props {
  open: boolean
  draft: ScreeningRuleDraft
  questions: SourceQuestion[]
  onSave: (draft: ScreeningRuleDraft) => void
  onClose: () => void
}

/** Editor for one screening rule: an AND of conditions. */
const DialogScreeningRule: React.FC<Props> = ({
  open,
  draft,
  questions,
  onSave,
  onClose
}: Props) => {
  const { classes } = useStyles()
  const [rows, setRows] = useState<ScreeningRuleRow[]>(
    draft.rows.length > 0 ? draft.rows : [emptyRow(true)]
  )
  const working: ScreeningRuleDraft = { ...draft, rows }
  const screenOutFirst = questionsThatScreenOutFirst(working, questions)
  const targeting = targetingQuestionsReferenced(working, questions)

  return (
    <Dialog
      open={open}
      title="Screening rule"
      maxWidth={DialogMaxWidth.Md}
      primaryButtonText="Save rule"
      primaryButtonDisabled={!isDraftComplete(working)}
      primaryButtonClick={() => onSave(working)}
      onClose={onClose}
      fullWidth
    >
      <Grid>
        <Grid
          container
          alignItems="center"
          gap="4px"
          className={classes.subtitleContainer}
        >
          <Text size={TextSize.m} highlight={TextHighlight.Background}>
            Screen the respondent out if:
          </Text>
          <InfoIcon text="Every condition must hold, and the rule is checked as soon as every question it references has been answered. For OR, add a second rule — any rule that matches screens the respondent out." />
        </Grid>

        <AudienceConditionRows
          rows={rows}
          questions={questions}
          onChange={setRows}
          ariaPrefix="Screening rule"
          defaultNegated
        />

        {screenOutFirst.length > 0 && (
          <Grid className={classes.warningText}>
            <Text size={TextSize.s} highlight={TextHighlight.Error}>
              {`${screenOutFirst
                .map((q) => q.label)
                .join(
                  ', '
                )} screens out on its own — a respondent who picks a non-qualifying option is screened out there, before this rule is checked. Qualify every option there for the rule to decide.`}
            </Text>
          </Grid>
        )}

        {targeting.length > 0 && (
          <Grid className={classes.warningText}>
            <Text size={TextSize.s} highlight={TextHighlight.Background}>
              {`${targeting
                .map((q) => q.label)
                .join(
                  ', '
                )} is a targeting question. Moving its screen in-survey changes incidence, so the quote will change.`}
            </Text>
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

export default DialogScreeningRule
